import React from 'react';
import { motion } from 'motion/react';

interface ButtonProps {
  children: React.ReactNode;
  onClick?: () => void;
  variant?: 'primary' | 'secondary' | 'outline' | 'ghost' | 'gradient' | 'cta' | 'cta-glass' | 'cta-border' | 'danger-outline';
  size?: 'sm' | 'md' | 'lg' | 'xl';
  className?: string;
  disabled?: boolean;
  fullWidth?: boolean;
  icon?: React.ReactNode;
  href?: string;
  target?: string;
  rel?: string;
  isLoading?: boolean;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
  type?: 'button' | 'submit' | 'reset';
}

const Button: React.FC<ButtonProps> = ({
  children,
  onClick,
  variant = 'primary',
  size = 'md',
  className = '',
  disabled = false,
  fullWidth = false,
  icon,
  href,
  target,
  rel,
  isLoading = false,
  leftIcon,
  rightIcon,
  type = 'button',
}) => {
  const baseClasses = `
    inline-flex items-center justify-center font-medium rounded-full
    transition-all duration-300 ease-in-out
    focus:outline-none focus:ring-2 focus:ring-primary/50 focus:ring-offset-2
    disabled:opacity-50 disabled:cursor-not-allowed relative overflow-hidden
    ${fullWidth ? 'w-full' : ''}
  `;

  const sizeClasses = {
    sm: 'px-4 py-1.5 text-sm h-8',
    md: 'px-6 py-2 text-sm h-10',
    lg: 'px-8 py-2.5 text-base h-12',
    xl: 'px-10 py-3 text-lg h-14',
  };

  const variantClasses = {
    primary: `
      bg-gradient-to-r from-primary to-accent text-white shadow-md hover:shadow-lg
      hover:shadow-primary/30 hover:scale-105 active:scale-95
    `,
    secondary: `
      bg-white/10 backdrop-blur-sm border border-white/20 text-gradient
      hover:bg-gradient-to-r hover:from-primary hover:to-accent
      hover:scale-105 active:scale-95
    `,
    outline: `
      bg-transparent border border-primary text-gradient
      hover:bg-gradient-to-r hover:from-primary hover:to-accent
      hover:scale-105 active:scale-95
    `,
    ghost: `
      bg-transparent text-gradient hover:bg-gradient-to-r
      hover:from-primary/20 hover:to-accent/20 hover:scale-105 active:scale-95
    `,
    gradient: `
      bg-gradient-hero text-white shadow-button hover:shadow-button-hover
      hover:scale-105 active:scale-95
    `,
    cta: `
      bg-white/95 backdrop-blur-md text-primary border border-white/50 shadow-modern
      hover:bg-white hover:shadow-button-hover hover:scale-105 active:scale-95
      relative overflow-hidden before:absolute before:inset-0 before:bg-gradient-to-r before:from-transparent before:via-white/20 before:to-transparent 
      before:translate-x-[-100%] hover:before:translate-x-[100%] before:transition-transform before:duration-700
    `,
    'cta-glass': `
      bg-white/10 backdrop-blur-md border border-white/30 text-white shadow-glow
      hover:bg-white/20 hover:border-white/50 hover:shadow-button-hover hover:scale-105 active:scale-95
      relative before:absolute before:inset-0 before:rounded-full before:bg-gradient-to-r before:from-white/5 before:to-transparent 
      before:opacity-0 hover:before:opacity-100 before:transition-all before:duration-300
    `,
    'danger-outline': `
      bg-transparent border border-red-300 bg-gradient-to-r from-red-500 to-red-600 bg-clip-text text-transparent
      hover:border-red-400 hover:from-red-600 hover:to-red-700 hover:bg-red-50
      hover:scale-105 active:scale-95
    `
  };

  const combinedClasses = `
    ${baseClasses}
    ${sizeClasses[size]}
    ${variantClasses[variant]}
    ${className}
  `;

  const content = (
    <>
      {(leftIcon || icon) && <span className="mr-2">{leftIcon || icon}</span>}
      {isLoading ? (
        <div className="w-5 h-5 border-2 border-current border-t-transparent rounded-full animate-spin" />
      ) : (
        children
      )}
      {rightIcon && <span className="ml-2">{rightIcon}</span>}
    </>
  );

  if (href) {
    return (
      <motion.a
        whileHover={{ scale: disabled || isLoading ? 1 : 1.05 }}
        whileTap={{ scale: disabled || isLoading ? 1 : 0.95 }}
        href={href}
        target={target}
        rel={rel}
        className={combinedClasses}
        onClick={onClick}
      >
        {content}
      </motion.a>
    );
  }

  return (
    <motion.button
      whileHover={{ scale: disabled || isLoading ? 1 : 1.05 }}
      whileTap={{ scale: disabled || isLoading ? 1 : 0.95 }}
      type={type}
      onClick={onClick}
      disabled={disabled || isLoading}
      className={combinedClasses}
    >
      {content}
    </motion.button>
  );
};

export default Button;